import React, { useState } from 'react';
import { Pie, PieChart as RechartPieChart, Sector, ResponsiveContainer, Legend, Cell } from 'recharts';
import { Colors } from '@inplayer-org/inplayer-ui';
import toLower from 'lodash/toLower';
import startCase from 'lodash/startCase';

// data
import { colors } from 'data/colors';

// utils
import formatNumber from 'utils/formatNumber';

export interface ChartProps {
  data: Array<any>;
  name: string;
  value: string;
}

const DonutPieChart: React.FC<ChartProps> = ({ data, name, value }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const renderActiveShape = (props: any) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, payload, percent, fill } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={Colors.fontDarkGray}>
          {startCase(toLower(payload[name]))}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">{`${formatNumber(
          payload[value]
        )}`}</text>
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill={fill}>
          {`${(percent * 100).toFixed(2)}%`}
        </text>
      </g>
    );
  };

  const onPieEnter = (_data: any, index: React.SetStateAction<number>) => {
    setActiveIndex(index);
  };

  return (
    <ResponsiveContainer width="100%" aspect={1.7}>
      <RechartPieChart>
        <Pie
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          data={data}
          innerRadius="45%"
          outerRadius="60%"
          fill={Colors.gray}
          dataKey={value}
          onMouseEnter={onPieEnter}
        >
          {data?.map((entry, index) => (
            <Cell key={`${entry[name]}${entry[value]}`} fill={colors[index]} />
          ))}
        </Pie>

        <Legend
          payload={data?.map((item, index) => ({
            id: item[name],
            type: 'square',
            value: `${startCase(toLower(item[name]))}`,
            color: colors[index],
          }))}
          verticalAlign="bottom"
          height={36}
        />
      </RechartPieChart>
    </ResponsiveContainer>
  );
};

export default DonutPieChart;
